import React, { useState, useEffect } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Menu,
	MenuItem,
	Switch,
	Typography,
} from '@barracuda-internal/bds-core-v2';
import MenuVertical from '@barracuda-internal/bds-core-v2/dist/Icons/Core/MenuVertical';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import { Policy } from 'src/types/policyTypes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'src/redux/store';
import { openEditPolicyDrawer } from 'src/features/policyengine/policyEngineSlice';
import { deletePolicy, togglePolicyStatus, updatePriorities } from 'src/features/policyengine/thunks';

type PolicyRowActionsProps = {
	feed: Policy;
};

const PolicyRowActions = ({ feed }: PolicyRowActionsProps): React.ReactElement => {
	const dispatch = useDispatch<AppDispatch>();
	const { policyChange, policyFeeds } = useSelector((state: RootState) => state.policyEngine);

	const [enableTxt, setEnableTxt] = useState(feed.disabled === false ? 'Enabled' : 'Enable');
	const [showDelete, setShowDelete] = useState(false);
	const { api } = useAppToolchain();
	const { auth } = useAuth();
	const [statusChecked, setStatusChecked] = useState(!feed.disabled);

	useEffect(() => {
		const newCheckedState = !feed.disabled;
		setStatusChecked(newCheckedState);
		updateEnableText(newCheckedState);
	}, [feed.id, feed.disabled]);

	const accountId = auth.accountDetails.account.id;

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	function handleToggleChange(e: React.ChangeEvent<HTMLInputElement>, policy: Policy) {
		if (policy.isDefault) return;

		const prevCheckedState = statusChecked;
		const newCheckedState = e.target.checked;

		setStatusChecked(newCheckedState);
		updateEnableText(newCheckedState);

		dispatch(
			togglePolicyStatus({
				api,
				accountId,
				policy,
				disabled: !newCheckedState,
			})
		).unwrap().catch(() => {
			setStatusChecked(prevCheckedState);
			updateEnableText(prevCheckedState);
		});
	}

	function updateEnableText(policyEnabled: boolean) {
		policyEnabled ? setEnableTxt('Enabled') : setEnableTxt('Enable');
	}

	function handleModification(policy: Policy) {
		setAnchorEl(null);
		setShowDelete(false);
		dispatch(openEditPolicyDrawer(policy));
	}

	function cancelDelete() {
		setShowDelete(false);
	}

	const handleDelete = () => {
		setAnchorEl(null);
		setShowDelete(true);
	};

	function handleDeletePolicy(policy: Policy) {
		if (!policy?.id) return;

		dispatch(
			deletePolicy({
				api,
				accountId,
				policyId: policy.id,
				policyName: policy.name,
			})
		)
			.unwrap()
			.then(() => {
				const remainingFeeds = policyFeeds.filter((f) => f.id !== policy.id);
				const priorityChange = remainingFeeds.map((f, index) => ({
					id: f.id,
					priority: index + 1,
				}));

				if (priorityChange.length > 0) {
					dispatch(updatePriorities({ api, accountId, priorities: priorityChange, skipToast: true }));
				}
				setShowDelete(false);
			})
			.catch(() => {
				// Keep dialog open so user can retry or cancel after failed delete
			});
	}

	return (
		<Box sx={{ display: 'flex', alignItems: 'center' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', marginRight: '10px' }}>
				<Switch
					size="small"
					disabled={feed?.isDefault === true}
					checked={statusChecked}
					onChange={(e) => handleToggleChange(e, feed)}
				/>
				<Typography component="span" variant="body1" noWrap sx={{ marginLeft: '8px' }}>
					{enableTxt}
				</Typography>
			</Box>
			{!policyChange && (
				<Box>
					<IconButton
						aria-label="more"
						id="long-button"
						aria-controls={open ? 'long-menu' : undefined}
						aria-expanded={open ? 'true' : undefined}
						aria-haspopup="true"
						onClick={handleClick}
					>
						<MenuVertical />
					</IconButton>
					<Menu
						id="long-menu"
						anchorEl={anchorEl}
						open={open}
						onClose={handleClose}
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'left',
						}}
						transformOrigin={
							feed?.isDefault
								? { vertical: 45, horizontal: 45 }
								: { vertical: 80, horizontal: 65 }
						}
					>
						<MenuItem onClick={() => handleModification(feed)}>Edit</MenuItem>
						{!feed?.isDefault && (
							<MenuItem onClick={() => handleDelete()}>Delete</MenuItem>
						)}
					</Menu>
				</Box>
			)}
			<Dialog open={showDelete} onClose={cancelDelete}>
				<DialogTitle>
					<Typography component="span" variant="h3" color="textPrimary">
						Delete Policy
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Typography variant="body1" color="textPrimary">
						Do you want to delete &quot;{feed?.name}&quot; permanently?
					</Typography>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'flex-end', padding: 2 }}>
					<Button
						bdsType="interactiveNeutral"
						onClick={cancelDelete}
					>
						No
					</Button>
					<Button
						bdsType="interactiveEmphasis"
						onClick={() => handleDeletePolicy(feed)}
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PolicyRowActions;
